// src/components/report_details/ReportDetails.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { languageFlags } from '../../constants/languageFlags';
import { FileText, Download } from 'lucide-react';

interface Domain { id: number; domain_name: string; }
interface URL { id: number; url: string; }

interface ReportDetailsProps {
  domains: Domain[];
  urls: URL[];
  created_at?: Date;
  reportDownloadUrls: Record<string, string>;
  summaryDownloadUrls?: Record<string, string>; // 👈 nuevo
}

const ReportDetails: React.FC<ReportDetailsProps> = ({
  domains,
  urls,
  created_at,
  reportDownloadUrls,
  summaryDownloadUrls = {},
}) => {
  const { t, i18n } = useTranslation();

  // idiomas disponibles (unión de report + summary)
  const availableLanguages = useMemo(() => {
    const set = new Set<string>([
      ...Object.keys(reportDownloadUrls || {}),
      ...Object.keys(summaryDownloadUrls || {}),
    ]);
    return Array.from(set);
  }, [reportDownloadUrls, summaryDownloadUrls]);

  // idioma inicial: i18n → base → en → primero
  const preferred = useMemo(() => {
    const full = i18n.language || 'en';
    const base = full.split('-')[0];
    if (availableLanguages.includes(full)) return full;
    if (availableLanguages.includes(base)) return base;
    if (availableLanguages.includes('en')) return 'en';
    return availableLanguages[0] || 'en';
  }, [availableLanguages, i18n.language]);

  const [selectedLanguage, setSelectedLanguage] = useState<string>(preferred);
  const [downloadError, setDownloadError] = useState<string>('');

  // si cambian las urls disponibles, re-evaluamos default
  useEffect(() => setSelectedLanguage(preferred), [preferred]);

  // helpers
  const openOrError = (url?: string) => {
    if (!url) {
      setDownloadError(t('reportDetails.no_download_available'));
      setTimeout(() => setDownloadError(''), 4000);
      return;
    }
    if (url === 'test') {
      setDownloadError(t('reportDetails.test_download_error'));
      setTimeout(() => setDownloadError(''), 4000);
      return;
    }
    window.open(url, '_blank');
  };

  const handleDownloadReport = () => {
    openOrError(reportDownloadUrls[selectedLanguage]);
  };

  const handleViewSummary = () => {
    openOrError(summaryDownloadUrls[selectedLanguage]);
  };

  // fecha
  const formattedDate = (() => {
    if (!created_at) return t('reportDetails.no_date');
    const d = new Date(created_at);
    if (isNaN(d.getTime())) return t('reportDetails.no_date');
    return new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
  })();

  const hasSummaryInLang = Boolean(summaryDownloadUrls[selectedLanguage]);
  const hasReportInLang = Boolean(reportDownloadUrls[selectedLanguage]);

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-[0_8px_24px_rgba(15,23,42,0.06)]">
      {/* título */}
      <div className="flex items-center mb-6">
        <div className="w-2 h-7 bg-gray-800 mr-4 rounded" />
        <h3 className="text-2xl font-bold">{t('reportDetails.title')}</h3>
      </div>

      {/* dominios */}
      <div>
        <h4 className="font-semibold">{t('reportDetails.domains_title')}</h4>
        <ul className="list-disc pl-5">
          {domains.map((d) => <li key={d.id}>{d.domain_name}</li>)}
        </ul>
      </div>

      {/* urls */}
      <div className="mt-4">
        <h4 className="font-semibold">{t('reportDetails.urls_title')}</h4>
        <ul className="list-disc pl-5">
          {urls.map((u) => <li key={u.id}>{u.url}</li>)}
        </ul>
      </div>

      {/* fecha */}
      <div className="mt-4">
        <h4 className="font-semibold">{t('reportDetails.created_at_label')}</h4>
        <p>{formattedDate}</p>
      </div>

      {/* acciones */}
      <div className="mt-6">
        <p className="text-gray-700">{t('reportDetails.download_info')}</p>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* botones */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadReport}
              disabled={!hasReportInLang}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-white font-semibold transition
                ${hasReportInLang ? 'bg-slate-900 hover:bg-slate-800' : 'bg-slate-300 cursor-not-allowed'}`}
            >
              <Download className="h-5 w-5" />
              {t('reportDetails.download_button')}
            </button>

            <button
              onClick={handleViewSummary}
              disabled={!hasSummaryInLang}
              className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium transition
                ${hasSummaryInLang
                  ? 'border-slate-200 bg-gradient-to-b from-white to-slate-50 text-slate-700 hover:shadow-sm'
                  : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'}`}
              title={t('unpaidReportCard.viewSummary', 'Ver resumen')}
            >
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-50">
                <FileText className="h-3.5 w-3.5 text-blue-600" />
              </span>
              {t('unpaidReportCard.viewSummary', 'Ver resumen')}
            </button>
          </div>

          {/* selector idioma */}
          <div className="flex items-center gap-2">
            {languageFlags[selectedLanguage] && (
              <img
                src={languageFlags[selectedLanguage]}
                alt={t('reportDetails.language_flag_alt')}
                className="w-6 h-6 rounded object-cover"
              />
            )}
            <div className="relative">
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="appearance-none pl-4 pr-8 py-2 bg-white text-slate-800 font-semibold rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-600"
              >
                {availableLanguages.map((lang) => (
                  <option key={lang} value={lang}>
                    {t(`reportDetails.language.${lang.toLowerCase()}`, lang.toUpperCase())}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <svg className="h-4 w-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M7 7l3 3 3-3 1 1-4 4-4-4 1-1z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {downloadError && (
          <div className="mt-4 text-rose-600 font-semibold">{downloadError}</div>
        )}
      </div>
    </div>
  );
};

export default ReportDetails;
